import * as React from 'react';
import { Route } from 'react-router';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import { CatalogEntityPage, CatalogIndexPage, catalogPlugin } from '@backstage/plugin-catalog';
import { CatalogImportPage, catalogImportPlugin } from '@backstage/plugin-catalog-import';
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import { TechRadarPage } from '@backstage-community/plugin-tech-radar';
import {
  DefaultTechDocsHome, TechDocsIndexPage, techdocsPlugin, TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { NotificationsPage } from '@backstage/plugin-notifications';
import {
  ProxiedSignInPage, SignInProviderConfig, SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/frontend-defaults';
import { FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import {
  oktaAuthApiRef, discoveryApiRef, useApi, configApiRef,
  createFrontendModule,
} from '@backstage/frontend-plugin-api';
import type { BackstageUserIdentity, IdentityApi, ProfileInfo } from '@backstage/core-plugin-api';
import * as Sentry from '@sentry/react';
import { LocalStorageCache } from '@eng-portal/czi-extensions-react';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { SignInPageBlueprint } from '@backstage/plugin-app-react';
import { convertLegacyAppRoot } from '@backstage/core-compat-api';
import { AmazonProfilePickerFieldExtension } from './scaffolder/AmazonProfilePicker';
import { searchPage } from './components/search/SearchPage';
import { entityPage } from './components/catalog/EntityPage';
import { apiExtensions, catalogApiExtensions } from './apis';
import { HiddenRepoUrlPickerExtension } from './scaffolder/HiddenRepoUrlPicker';
import { HomePage } from './components/home/HomePage';
import { OktaAppPickerFieldExtension } from './scaffolder/OktaAppPicker';
import { DynamicFieldPickerExtension } from './scaffolder/DynamicFieldPicker';
import { UpdateAnnouncementDetailsExtension } from './scaffolder/UpdateAnnouncementDetails';
import { LocalDateTimePickerExtension } from './scaffolder/LocalDateTimePicker';
import { ApproverSetPickerExtension } from './scaffolder/ApproverSetPicker';
import { CatalogTagPickerExtension } from './scaffolder/CatalogTagPicker';
import { MultiEmailPickerExtension } from './scaffolder/MultiEmailPicker';
import { AwsAccountNameInputExtension } from './scaffolder/AwsAccountNameInput';
import { StrictEnumPickerExtension } from './scaffolder/StrictEnumPicker';
import { setTokenCookie } from './cookieAuth';
import { HelpCenterPage } from './components/help/HelpCenterPage';
import { InfraRequestsPage } from './components/infraRequests/InfraRequestsPage';
import { navModule } from './modules/nav';

const oktaProvider: SignInProviderConfig = {
  id: 'okta-auth-provider',
  title: 'okta',
  message: 'Sign in using Okta',
  apiRef: oktaAuthApiRef,
};

const signInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => (props): JSX.Element => {
      const { onSignInSuccess } = props;
      const configApi = useApi(configApiRef);
      const isE2eMode = configApi.getOptionalBoolean('app.e2eTestMode') ?? false;
      const discoveryApi = useApi(discoveryApiRef);

      if (isE2eMode) {
        return <ProxiedSignInPage {...props} provider="guest" />;
      }

      return (
        <SignInPage
          {...props}
          auto
          provider={oktaProvider}
          onSignInSuccess={async (identityApi: IdentityApi): Promise<void> => {
            // create a wrapper so we can clear the localstorage cache when a user signs out
            const identityApiWrapper = {
              getProfileInfo(): Promise<ProfileInfo> {
                return identityApi.getProfileInfo();
              },
              getBackstageIdentity(): Promise<BackstageUserIdentity> {
                return identityApi.getBackstageIdentity();
              },
              getCredentials(): Promise<{ token?: string }> {
                return identityApi.getCredentials();
              },
              async signOut(): Promise<void> {
                LocalStorageCache.clear();
                await identityApi.signOut();
              },
            };
            setTokenCookie(
              await discoveryApi.getBaseUrl('cookie'),
              identityApiWrapper,
            );
            Sentry.setUser({ username: (await identityApiWrapper.getBackstageIdentity()).userEntityRef });
            onSignInSuccess(identityApiWrapper);
          }}
        />
      );
    },
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    <Route path="/catalog" element={<CatalogIndexPage initialKind="Group" />} />
    <Route path="/catalog/:namespace/:kind/:name" element={<CatalogEntityPage />}>
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />}>
      <DefaultTechDocsHome />
    </Route>
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/help" element={<HelpCenterPage />} />
    <Route
      path="/create"
      element={(
        <ScaffolderPage
          headerOptions={{
            title: 'Automations',
            subtitle: 'Workflow automations to help you accomplish more with less effort',
          }}
          groups={[
            {
              title: 'Infra Requests',
              filter: (entity): boolean => entity?.metadata?.tags?.includes('infra-requests') ?? false,
            },
            {
              title: 'Core Routine',
              filter: (entity): boolean => entity?.metadata?.tags?.includes('core-routine') ?? false,
            },
          ]}
        />
    )}
    >
      <ScaffolderFieldExtensions>
        <AmazonProfilePickerFieldExtension />
        <HiddenRepoUrlPickerExtension />
        <OktaAppPickerFieldExtension />
        <DynamicFieldPickerExtension />
        <UpdateAnnouncementDetailsExtension />
        <LocalDateTimePickerExtension />
        <ApproverSetPickerExtension />
        <CatalogTagPickerExtension />
        <MultiEmailPickerExtension />
        <AwsAccountNameInputExtension />
        <StrictEnumPickerExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route path="/tech-radar" element={<TechRadarPage width={1500} height={800} />} />
    <Route
      path="/catalog-import"
      element={(
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      )}
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route path="/infra-requests" element={<InfraRequestsPage />} />
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
  </FlatRoutes>
);

const convertedRootFeatures = convertLegacyAppRoot(routes, { entityPage });

const appExtensionsModule = createFrontendModule({
  pluginId: 'app',
  extensions: [signInPage, ...apiExtensions],
});

const catalogExtensionsModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [...catalogApiExtensions],
});

const app = createApp({
  features: [
    appExtensionsModule,
    catalogExtensionsModule,
    navModule,
    ...convertedRootFeatures,
  ],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
});

export default app.createRoot();
